import { CustomWindow, SportType, WidgetContextProps } from '../../types';
import BookmakerModel from '@mediaplatform/sportal365-odds-utils/types/models/bookmaker.model';
import OddsModel from '@mediaplatform/sportal365-odds-utils/types/models/odds.model';

const populateWindowProps = (widgetContextProps: WidgetContextProps, formattedOdds: Record<string, number>) => {
  const { widgetId, widgetSport, widgetCuid } = widgetContextProps;
  const windObjCopy = window as any as CustomWindow;

  // add initial smpWidgetsOdds to window object (if missing only)
  if (!windObjCopy.smpWidgetsOdds) {
    windObjCopy.smpWidgetsOdds = {};
  }

  // add initial sport to smpWidgetsOdds object (if missing only)
  if (!windObjCopy.smpWidgetsOdds[widgetSport]) {
    windObjCopy.smpWidgetsOdds[widgetSport] = {};
  }

  // add initial widget-id to smpWidgetsOdds[SPORT] object (if missing only)
  const smpWidgetsOddsBySport = windObjCopy.smpWidgetsOdds[widgetSport] ?? {};
  if (!smpWidgetsOddsBySport[widgetId]) {
    smpWidgetsOddsBySport[widgetId] = {};
  }

  // add initial widget-cuid to smpWidgetsOdds[SPORT][WIDGET-ID] object (if missing only)
  const smpWidgetsOddsByWidgetId = smpWidgetsOddsBySport[widgetId] ?? {};
  if (!smpWidgetsOddsByWidgetId[widgetCuid]) {
    smpWidgetsOddsByWidgetId[widgetCuid] = {};
  }

  smpWidgetsOddsByWidgetId[widgetCuid] = formattedOdds;
};

const filterOddsByProviderId = (odds: OddsModel[], dataOddsBettingId?: string | null): BookmakerModel[] => {
  let result = [...odds];
  if (dataOddsBettingId) {
    const bettingIds: string[] = dataOddsBettingId
      .toString()
      .split(',')
      .map((providerId) => providerId.trim());
    result = result.filter((odd) => odd?.bookmaker?.id && bettingIds.includes(odd.bookmaker.id));
  }

  return result.map((odd) => odd.bookmaker).filter((bookmaker): bookmaker is BookmakerModel => Boolean(bookmaker));
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const extractBookmakersDisplayed = (data: any, dataOddsBettingIds: string | undefined | null, canDisplayMoreOddsInMatch: boolean) => {
  const bookmakersResult: BookmakerModel[] = [];

  // Recursive function to find and add the first bookmaker of each match
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function findBookmaker(data: any) {
    // Base case: if data is not an object or is null, return
    if (typeof data !== 'object' || data === null) {
      return;
    }

    // Safely checking for 'odds' property and if it's an array with at least one bookmaker
    if (Array.isArray(data.odds) && data.odds.length > 0) {
      let bookmakers: BookmakerModel[] = filterOddsByProviderId(data.odds, dataOddsBettingIds);
      bookmakers = canDisplayMoreOddsInMatch ? bookmakers : [bookmakers[0]];
      bookmakers.forEach((bookmakerResult) => bookmakersResult.push(bookmakerResult));
      return; // Stop searching for more bookmaker
    }

    // If the data is an array, iterate over each element
    if (Array.isArray(data)) {
      for (let i = 0; i < data.length; i++) {
        findBookmaker(data[i]);
      }
    } else {
      // If the data is an object, iterate over each property
      Object.values(data).forEach((value) => {
        findBookmaker(value);
      });
    }
  }

  findBookmaker(data); // Start the recursive search
  return bookmakersResult;
};

const formatOddsCount = (bookmakers: BookmakerModel[]) => {
  const stackedOdds: Record<string, number> = {};

  bookmakers.forEach((bookmaker) => {
    const bookmakerId: string = bookmaker?.id ?? 'unknown';
    stackedOdds[bookmakerId] = stackedOdds[bookmakerId] ? stackedOdds[bookmakerId] + 1 : 1;
  });

  return stackedOdds;
};

export const stackOdds = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any, // data is the response from the API
  dataWidgetSport: string | undefined,
  dataWidgetId: string | undefined,
  dataWidgetCuid: string | undefined,
  dataOddsBettingId: string | null | undefined,
  canDisplayMoreOddsInMatch = false, // in single event widgets we can show more than one bookmaker for the match
) => {
  if (dataWidgetSport && dataWidgetId && dataWidgetCuid) {
    const extractedBookmakers = extractBookmakersDisplayed(data, dataOddsBettingId, canDisplayMoreOddsInMatch);
    const formattedOdds = formatOddsCount(extractedBookmakers);
    populateWindowProps(
      {
        widgetId: dataWidgetId,
        widgetSport: dataWidgetSport as SportType,
        widgetCuid: dataWidgetCuid,
      },
      formattedOdds,
    );
  }
};
